import React, { useMemo } from "react";
import { Button, ConfigProvider, Modal, Progress, Segmented, Space, Table, Tag, Tooltip, Typography, theme } from "antd";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import style from "css/admin/component.module.css";

/**
 * 작업 결과 모달 (요약 + 테이블 + 확인)
 *
 * @param {boolean} open
 * @param {string} title
 * @param {string} description optional
 * @param {object} summary { total, success, fail }
 * @param {Array<object>} rows table rows (기존 승인/해제/연장 모달과 동일한 row 정보 + RESULT_STATUS/RESULT_MESSAGE)
 * @param {Function} onConfirm
 */
function ResultModal({ open, title, description, summary, rows, onConfirm }) {
  const { token } = theme.useToken();
  const [viewMode, setViewMode] = React.useState("all");

  const allRows = useMemo(() => {
    return (rows || []).map((r) => ({
      key: r?.APPLICATION_ID ?? r?.key,
      ...r,
    }));
  }, [rows]);

  const dataSource = useMemo(() => {
    if (viewMode === "fail") return allRows.filter((r) => r?.RESULT_STATUS === "fail");
    return allRows;
  }, [allRows, viewMode]);

  const columns = useMemo(
    () => [
      { title: "학번", dataIndex: "MEMBER_ID", key: "MEMBER_ID", width: 110 },
      { title: "이름", dataIndex: "MEMBER_NM", key: "MEMBER_NM", width: 120 },
      { title: "학과", dataIndex: "DEPARTMENT_NM", key: "DEPARTMENT_NM", width: 160 },
      { title: "라이선스", dataIndex: "LICENSE_NM", key: "LICENSE_NM" },
      {
        title: "결과",
        dataIndex: "RESULT_STATUS",
        key: "RESULT_STATUS",
        width: 72,
        align: "center",
        render: (status, record) => {
          const msg = record?.RESULT_MESSAGE ? String(record.RESULT_MESSAGE) : "";
          if (status === "success") {
            const node = <CheckOutlined style={{ fontSize: 18, color: token.colorSuccess }} />;
            return <Tooltip title={msg || "성공"}>{node}</Tooltip>;
          }
          if (status === "fail") {
            const node = <CloseOutlined style={{ fontSize: 18, color: token.colorError }} />;
            return <Tooltip title={msg || "실패"}>{node}</Tooltip>;
          }
          return (
            <span style={{ color: "#666", fontWeight: 600 }} title={msg || "알수없음"}>
              -
            </span>
          );
        },
      },
    ],
    [token.colorError, token.colorSuccess]
  );

  const total = Number(summary?.total || 0);
  const success = Number(summary?.success || 0);
  const fail = Number(summary?.fail || 0);
  const successRate = total > 0 ? Math.round((success / total) * 100) : 0;

  return (
    <Modal
      title={<span className={style.searchItem_label} style={{ fontSize: 18 }}>{title || "결과"}</span>}
      open={open}
      closable={false}
      maskClosable={false}
      footer={
        <ConfigProvider
          theme={{
            token: { colorPrimary: "#527ff2" },
            components: {
              Button: {
                colorPrimaryHover: "#4a72de",
                colorPrimaryActive: "#3f66c8",
              },
            },
          }}
        >
          <div style={{ display: "flex", gap: 8, width: "100%" }}>
            <Button
              key="confirm"
              type="primary"
              onClick={onConfirm}
              style={{ flex: 1, height: 40, borderRadius: 5, fontSize: 18 }}
            >
              확인
            </Button>
          </div>
        </ConfigProvider>
      }
      styles={{ content: { borderRadius: 12 }, mask: { backdropFilter: "none" } }}
      width={820}
      centered
    >
      {description ? (
        <Typography.Text style={{ display: "block", marginBottom: 8, fontSize: 14, color: "#33353b" }}>
          {description}
        </Typography.Text>
      ) : null}

      <Space direction="vertical" size={8} style={{ width: "100%", marginBottom: 12 }}>
        <Space wrap size={8} style={{ width: "100%", justifyContent: "space-between" }}>
          <Space wrap size={8}>
            <Tag style={{ marginInlineEnd: 0 }}>총 <span style={{ fontWeight: 800, color: token.colorText }}>{total}</span>건</Tag>
            <Tag color="success" style={{ marginInlineEnd: 0 }}>
              성공 <span style={{ fontWeight: 900 }}>{success}</span>건
            </Tag>
            <Tag color="error" style={{ marginInlineEnd: 0 }}>
              실패 <span style={{ fontWeight: 900 }}>{fail}</span>건
            </Tag>
          </Space>

          <Segmented
            size="small"
            value={viewMode}
            onChange={setViewMode}
            options={[
              { label: "전체", value: "all" },
              { label: `실패 (${fail})`, value: "fail" },
            ]}
          />
        </Space>

        <Progress
          percent={successRate}
          strokeColor={token.colorSuccess}
          trailColor={token.colorErrorBg}
          size="small"
          format={(p) => `성공률 ${p}%`}
        />
      </Space>

      <Table
        columns={columns}
        dataSource={dataSource}
        size="small"
        pagination={false}
        scroll={{ y: 360 }}
      />
    </Modal>
  );
}

export default ResultModal;


